import React from 'react'
import { Row, Col } from 'react-bootstrap'
import { useGetModelQuery } from '../../../services/modelsApi'
import { useGetProjectQuery } from '../../../services/projectsApi'
import BBoxTrainingProgressCard from '../ModelTrainingView/BBoxTrainingProgressCard'
import ClassificationTrainingProgressCard from '../ModelTrainingView/ClassificationTrainingProgressCard'
import styled from 'styled-components'
import { TrendingUp, BarChart3, Target, Award } from 'lucide-react'

const ViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0;
`

const QuickStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`

const StatCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  font-weight: 500;
`

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
`

const SectionCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`

const SectionTitle = styled.h5`
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

const MetricCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
  }
`

const MetricIcon = styled.div`
  color: #800085;
  margin-bottom: 0.25rem;
`

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
`

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`

interface CombinedTrainingPerformanceViewProps {
  modelId?: string
  projectId?: string
}

const CombinedTrainingPerformanceView: React.FC<CombinedTrainingPerformanceViewProps> = ({
  modelId,
  projectId,
}) => {
  const { data: model } = useGetModelQuery(modelId || '', {
    skip: !modelId,
  })

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  const isTraining = model?.state === 'TRAINING'
  const isTrained = model?.state === 'TRAINED'
  const isClassification = project?.annotation_type === 'classification'

  const gradients = React.useMemo(
    () => ({
      sources: 'linear-gradient(135deg, #402563, #114ee0)',
      stats: 'linear-gradient(135deg, #402563, #2042ba)',
    }),
    []
  )

  // Calculate training duration
  const calculateDuration = () => {
    if (!model?.train_started_at) return 'N/A'

    if (model?.train_ended_at && model.train_ended_at !== '0001-01-01T00:00:00Z') {
      const start = new Date(model.train_started_at)
      const end = new Date(model.train_ended_at)
      const durationMs = end.getTime() - start.getTime()
      const hours = Math.floor(durationMs / (1000 * 60 * 60))
      const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

      if (hours > 0) return `${hours}h ${minutes}m`
      return `${minutes}m`
    } else if (isTraining) {
      const start = new Date(model.train_started_at)
      const now = new Date()
      const durationMs = now.getTime() - start.getTime()
      const hours = Math.floor(durationMs / (1000 * 60 * 60))
      const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

      if (hours > 0) return `${hours}h ${minutes}m (ongoing)`
      return `${minutes}m (ongoing)`
    }

    return 'N/A'
  }

  // Get final performance metrics
  const getFinalMetrics = () => {
    if (!model?.metrics) return []

    const isClassificationModel = isClassification
    const metrics = model.metrics

    if (isClassificationModel) {
      const trainAcc = metrics['train:accuracy'] as number | undefined
      const valAcc = metrics['validation:accuracy'] as number | undefined
      return [
        { label: 'Validation Accuracy', value: valAcc, icon: Award },
        { label: 'Training Accuracy', value: trainAcc, icon: TrendingUp },
      ].filter((m) => m.value !== undefined)
    } else {
      const mAP = metrics['validation:mAP50-95'] as number | undefined
      const precision = metrics['validation:precision'] as number | undefined
      const recall = metrics['validation:recall'] as number | undefined
      const f1 = precision && recall ? (2 * precision * recall) / (precision + recall) : undefined

      return [
        { label: 'mAP', value: mAP, icon: Target },
        { label: 'Precision', value: precision, icon: BarChart3 },
        { label: 'Recall', value: recall, icon: Target },
        { label: 'F1 Score', value: f1, icon: TrendingUp },
      ].filter((m) => m.value !== undefined)
    }
  }

  // Get epoch info
  const getEpochInfo = () => {
    if (isClassification) {
      const trainingStats = model?.training_stats?.classification
      const entries = trainingStats?.training_job_metrics
        ? Object.values(trainingStats.training_job_metrics)[0]?.training_job_entries
        : []

      const currentEpoch = entries && entries.length > 0 ? entries.length : 0
      const totalEpochs =
        (model?.parameters?.hyperparameters?.hyperparameters?.training?.epochs?.value as number) ||
        0

      return { current: currentEpoch, total: totalEpochs }
    } else {
      const trainingStats = model?.training_stats?.bounding_box
      const entries = trainingStats?.training_job_metrics
        ? Object.values(trainingStats.training_job_metrics)[0]?.training_job_entries
        : []

      const currentEpoch = entries && entries.length > 0 ? entries.length : 0
      const totalEpochs =
        (model?.parameters?.hyperparameters?.hyperparameters?.training?.epochs?.value as number) ||
        0

      return { current: currentEpoch, total: totalEpochs }
    }
  }

  const epochInfo = getEpochInfo()
  const finalMetrics = getFinalMetrics()

  if (!model) {
    return (
      <ViewContainer>
        <SectionCard>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
            No model data available
          </p>
        </SectionCard>
      </ViewContainer>
    )
  }

  return (
    <ViewContainer>
      {/* Quick Summary Stats */}
      <SectionCard>
        <SectionTitle>Training Summary</SectionTitle>
        <QuickStatsGrid>
          <StatCard>
            <StatLabel>Training Time</StatLabel>
            <StatValue>{calculateDuration()}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Epochs</StatLabel>
            <StatValue>{epochInfo.total} epochs</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Status</StatLabel>
            <StatValue style={{ fontSize: '1rem' }}>{model.state}</StatValue>
          </StatCard>
          {isTrained &&
            finalMetrics.length > 0 &&
            (() => {
              // For BBox models, show F1 Score. For classification, show first metric
              const summaryMetric = isClassification
                ? finalMetrics[0]
                : finalMetrics.find((m) => m.label === 'F1 Score') || finalMetrics[0]

              return (
                <StatCard>
                  <StatLabel>{summaryMetric.label}</StatLabel>
                  <StatValue>
                    {typeof summaryMetric.value === 'number'
                      ? `${(summaryMetric.value * 100).toFixed(1)}%`
                      : 'N/A'}
                  </StatValue>
                </StatCard>
              )
            })()}
        </QuickStatsGrid>
      </SectionCard>

      {/* Configuration & Timeline - Side by Side */}
      {isTrained && model.parameters && (
        <Row className="g-4">
          <Col lg={6}>
            <SectionCard style={{ height: '100%' }}>
              <SectionTitle>Model Configuration</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Model Type:</span>
                  <strong style={{ color: '#ffffff' }}>
                    {model.parameters?.hyperparameters?.type?.replace(/_/g, ' ').toUpperCase() ||
                      'N/A'}
                  </strong>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Input Size:</span>
                  <strong style={{ color: '#ffffff' }}>
                    {(() => {
                      const hp = model.parameters?.hyperparameters
                      const inner = hp?.hyperparameters
                      if (hp?.type === 'object_detection') {
                        const imageSize = (inner as any)?.training?.image_shape?.value as
                          | number
                          | undefined
                        return imageSize ? `${imageSize}px` : 'N/A'
                      } else if (hp?.type === 'classification') {
                        const imageSize = (inner as any)?.training?.image_shape?.value as
                          | number
                          | undefined
                        return imageSize ? `${imageSize}px` : 'N/A'
                      }
                      return 'N/A'
                    })()}
                  </strong>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Data Augmentation:</span>
                  <strong style={{ color: '#ffffff' }}>
                    {(() => {
                      const hp = model.parameters?.hyperparameters
                      const inner = hp?.hyperparameters
                      if (hp?.type === 'object_detection') {
                        const aug = (inner as any)?.training?.augment?.value as boolean | undefined
                        return aug ? 'Enabled' : 'Disabled'
                      } else if (hp?.type === 'classification') {
                        const aug = (inner as any)?.training?.augment?.value as boolean | undefined
                        return aug ? 'Enabled' : 'Disabled'
                      }
                      return 'N/A'
                    })()}
                  </strong>
                </div>
              </div>
            </SectionCard>
          </Col>
          <Col lg={6}>
            <SectionCard style={{ height: '100%' }}>
              <SectionTitle>Training Timeline</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Started:</span>
                  <strong style={{ color: '#ffffff' }}>
                    {model.train_started_at
                      ? new Date(model.train_started_at).toLocaleString()
                      : 'N/A'}
                  </strong>
                </div>
                {model.train_ended_at && model.train_ended_at !== '0001-01-01T00:00:00Z' && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Completed:</span>
                    <strong style={{ color: '#ffffff' }}>
                      {new Date(model.train_ended_at).toLocaleString()}
                    </strong>
                  </div>
                )}
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Duration:</span>
                  <strong style={{ color: '#ffffff' }}>{calculateDuration()}</strong>
                </div>
              </div>
            </SectionCard>
          </Col>
        </Row>
      )}

      {/* Performance Metrics & Training Data - Side by Side */}
      {isTrained && (
        <Row className="g-4">
          {/* Left: Performance Metrics */}
          <Col lg={6}>
            <SectionCard style={{ height: '100%' }}>
              <SectionTitle>Final Performance Metrics</SectionTitle>
              <Row className="g-3">
                {finalMetrics.map((metric, idx) => (
                  <Col key={idx} xs={6}>
                    <MetricCard>
                      <MetricIcon>
                        <metric.icon size={24} />
                      </MetricIcon>
                      <MetricValue>
                        {typeof metric.value === 'number'
                          ? `${(metric.value * 100).toFixed(1)}%`
                          : 'N/A'}
                      </MetricValue>
                      <MetricLabel>{metric.label}</MetricLabel>
                    </MetricCard>
                  </Col>
                ))}
              </Row>
            </SectionCard>
          </Col>

          {/* Right: Training Data Composition */}
          <Col lg={6}>
            <SectionCard style={{ height: '100%' }}>
              <SectionTitle>Training Data Composition</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Training Data */}
                <div
                  style={{
                    background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '8px',
                    padding: '1rem',
                    borderLeft: '4px solid #10b981',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#10b981',
                      }}
                    />
                    <strong style={{ color: '#ffffff' }}>Training Dataset</strong>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {model.parameters?.train_metadata?.datasource?.length || 0} source
                      {(model.parameters?.train_metadata?.datasource?.length || 0) !== 1 ? 's' : ''}
                    </div>
                    {model.parameters?.train_metadata?.datasource &&
                      model.parameters.train_metadata.datasource.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                            fontSize: '0.875rem',
                          }}
                        >
                          {model.parameters.train_metadata.datasource
                            .slice(0, 4)
                            .map((source, idx) => (
                              <span
                                key={idx}
                                style={{
                                  background: 'rgba(16, 185, 129, 0.2)',
                                  color: '#10b981',
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  fontSize: '0.8rem',
                                  border: '1px solid rgba(16, 185, 129, 0.3)',
                                }}
                              >
                                {source}
                              </span>
                            ))}
                          {model.parameters.train_metadata.datasource.length > 4 && (
                            <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                              +{model.parameters.train_metadata.datasource.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                  </div>
                </div>

                {/* Validation Data */}
                {model.parameters?.validation_metadata?.datasource &&
                  model.parameters.validation_metadata.datasource.length > 0 && (
                    <div
                      style={{
                        background: '#1a1a1a',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '8px',
                        padding: '1rem',
                        borderLeft: '4px solid #3b82f6',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.75rem',
                        }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#3b82f6',
                          }}
                        />
                        <strong style={{ color: '#ffffff' }}>Validation Dataset</strong>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                          {model.parameters.validation_metadata.datasource.length} source
                          {model.parameters.validation_metadata.datasource.length !== 1 ? 's' : ''}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                            fontSize: '0.875rem',
                          }}
                        >
                          {model.parameters.validation_metadata.datasource
                            .slice(0, 4)
                            .map((source, idx) => (
                              <span
                                key={idx}
                                style={{
                                  background: 'rgba(59, 130, 246, 0.2)',
                                  color: '#3b82f6',
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  fontSize: '0.8rem',
                                  border: '1px solid rgba(59, 130, 246, 0.3)',
                                }}
                              >
                                {source}
                              </span>
                            ))}
                          {model.parameters.validation_metadata.datasource.length > 4 && (
                            <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                              +{model.parameters.validation_metadata.datasource.length - 4} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </SectionCard>
          </Col>
        </Row>
      )}

      {/* Training Curves - Full Width at Bottom */}
      {isTraining || isTrained ? (
        <SectionCard>
          <SectionTitle>Training Curves</SectionTitle>
          {isClassification ? (
            <ClassificationTrainingProgressCard model={model} gradient={gradients.stats} />
          ) : (
            <BBoxTrainingProgressCard model={model} gradient={gradients.stats} />
          )}
        </SectionCard>
      ) : (
        <SectionCard>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
            Training has not started yet
          </p>
        </SectionCard>
      )}
    </ViewContainer>
  )
}

export default CombinedTrainingPerformanceView
